import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useRole } from "@x-veloper/dataslot-auth";

type RoleType = "SuperAdmin" | "Admin" | "Member";

interface ViewAsRoleContextType {
  previewRole: RoleType | null;
  setPreviewRole: (role: RoleType | null) => void;
  clearPreviewRole: () => void;
  getEffectiveRole: () => string | null;
  isInPreviewMode: boolean;
  canUsePreview: boolean;
}

const ViewAsRoleContext = createContext<ViewAsRoleContextType | null>(null);

const PREVIEW_ROLE_KEY = "commission-preview-role";

interface ViewAsRoleProviderProps {
  children: ReactNode;
}

/**
 * ViewAsRole Context Provider
 * Allows SuperAdmin and Admin users to preview what other roles see
 *
 * Security:
 * - Only SuperAdmin and Admin can enter preview mode
 * - Preview role stored in sessionStorage (temporary, per-tab)
 * - Backend always uses actual user role (not affected by preview)
 */
export function ViewAsRoleProvider({ children }: ViewAsRoleProviderProps) {
  const actualRole = useRole();
  const [previewRole, setPreviewRoleState] = useState<RoleType | null>(null);

  // Check if user is SuperAdmin or Admin (only they can use preview feature)
  const canUsePreview = actualRole === "SuperAdmin" || actualRole === "Admin";

  // Load preview role from sessionStorage on mount
  useEffect(() => {
    if (canUsePreview) {
      const stored = sessionStorage.getItem(PREVIEW_ROLE_KEY);
      if (stored && (stored === "SuperAdmin" || stored === "Admin" || stored === "Member")) {
        setPreviewRoleState(stored as RoleType);
      }
    }
  }, [canUsePreview]);

  // Clear preview mode if user is not SuperAdmin or Admin
  useEffect(() => {
    if (!canUsePreview && previewRole) {
      clearPreviewRole();
    }
  }, [canUsePreview, previewRole]);

  const setPreviewRole = (role: RoleType | null) => {
    if (!canUsePreview) {
      console.warn("Only SuperAdmin and Admin users can use role preview feature");
      return;
    }

    setPreviewRoleState(role);
    if (role) {
      sessionStorage.setItem(PREVIEW_ROLE_KEY, role);
    } else {
      sessionStorage.removeItem(PREVIEW_ROLE_KEY);
    }
  };

  const clearPreviewRole = () => {
    setPreviewRoleState(null);
    sessionStorage.removeItem(PREVIEW_ROLE_KEY);
  };

  const getEffectiveRole = (): string | null => {
    // If in preview mode, return preview role
    if (previewRole && canUsePreview) {
      return previewRole;
    }
    // Otherwise return actual role
    return actualRole;
  };

  const isInPreviewMode = canUsePreview && previewRole !== null;

  return (
    <ViewAsRoleContext.Provider
      value={{
        previewRole,
        setPreviewRole,
        clearPreviewRole,
        getEffectiveRole,
        isInPreviewMode,
        canUsePreview,
      }}
    >
      {children}
    </ViewAsRoleContext.Provider>
  );
}

/**
 * Hook to access ViewAsRole context
 */
export function useViewAsRole() {
  const context = useContext(ViewAsRoleContext);
  if (!context) {
    throw new Error("useViewAsRole must be used within ViewAsRoleProvider");
  }
  return context;
}

/**
 * Hook to get the effective role (preview role if active, otherwise actual role)
 */
export function useEffectiveRole(): string | null {
  const { getEffectiveRole } = useViewAsRole();
  return getEffectiveRole();
}

/**
 * Hook to check if currently in preview mode
 */
export function useIsInPreviewMode(): boolean {
  const { isInPreviewMode } = useViewAsRole();
  return isInPreviewMode;
}

/**
 * Hook to check if effective role is admin (SuperAdmin or Admin)
 * Uses preview role if active
 */
export function useEffectiveIsAdmin(): boolean {
  const effectiveRole = useEffectiveRole();
  return effectiveRole === "SuperAdmin" || effectiveRole === "Admin";
}
